import type { ReactNode } from "react";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
// @ts-expect-error pure js
import TextArea from "../UI/TextArea";
import {
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconMinus,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { FieldArray, useField } from "formik";
// @ts-expect-error pure js
import useEdlMateriauxTemplates from "../../hooks/use-edl-materiaux-templates";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select";
import IconButton from "../UI/IconButton";
import Text from "../UI/Text";
import Status from "../UI/Status";
import type { Color } from "../UI/Text";
import EdlPhotoField from "./EdlPhotoField";
import EdlEtatPills from "./EdlEtatPills";
import EdlTagChips from "./EdlTagChips";
import EdlAddButton from "./EdlAddButton";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./EdlItemNomEtatInputs.module.css";

// @ts-expect-error pure js
import { ETAT_OPTIONS, EDL_ITEM_SUBITEM_VALUE } from "../../models/edl";

type EtatOption = { value: string; label: string };

const ETAT_STATUS_COLOR: Record<string, Color> = {
  "1": "info",
  "2": "success",
  "3": "warning",
  "4": "danger",
};

const ETAT_CARD_CLASS: Record<string, string> = {
  "1": styles.cardNeuf,
  "2": styles.cardGood,
  "3": styles.cardMedium,
  "4": styles.cardBad,
};

type EdlSubItem = {
  id?: number;
  nom?: string;
  note?: string | number;
};

type EdlItem = {
  id?: number;
  type?: string;
  nom?: string;
  note?: string | number;
  template_materiaux?: number | string;
  materiaux: string[];
  degradations: string[];
  quantite?: number | string;
  commentaire?: string;
  items?: EdlSubItem[];
  photos?: unknown[];
};

type EdlMateriauxTemplate = {
  id: number;
  nom: string;
  materiaux: string[];
  degradations: string[];
};

type EdlItemNomEtatInputsProps = {
  isChild?: boolean;
  item?: EdlItem;
  inputNamePrefix?: string;
  isTemplate?: boolean;
  onDelete?: () => void;
  onCopy?: () => void;
  dragHandle?: ReactNode;
  /** Élément déplié (en-tête éditable + détail). */
  open?: boolean;
  onToggle?: () => void;
};

function plural(count: number, singular: string) {
  return `${count} ${singular}${count > 1 ? "s" : ""}`;
}

/* Résumé d'un élément replié : ce qui a déjà été renseigné, en une ligne. */
function elementSummary(item: EdlItem | undefined, isTemplate?: boolean) {
  if (!item) return "";
  const parts: string[] = [];
  const quantite = Number(item.quantite);
  if (quantite > 1) parts.push(`× ${quantite}`);
  const materiaux = item.materiaux ?? [];
  const degradations = item.degradations ?? [];
  if (materiaux.length > 0) parts.push(materiaux.join(", "));
  if (!isTemplate) {
    if ((item.items?.length ?? 0) > 0) {
      parts.push(plural(item.items!.length, "sous-élément"));
    }
    if (degradations.length > 0) {
      parts.push(plural(degradations.length, "dégradation"));
    }
    if ((item.photos?.length ?? 0) > 0) {
      parts.push(plural(item.photos!.length, "photo"));
    }
  }
  return parts.join(" · ");
}

function EtatStatus({ note }: { note?: string | number }) {
  const key = note == null ? "" : String(note);
  const option = (ETAT_OPTIONS as EtatOption[]).find((o) => o.value === key);
  if (!option) {
    return <Status color="neutral" text="À faire" className={styles.status} />;
  }
  return (
    <Status
      color={ETAT_STATUS_COLOR[key]}
      text={option.label}
      className={styles.status}
    />
  );
}

function QuantiteStepper({ name }: { name: string }) {
  const [{ value }, , { setValue }] = useField<number | string | undefined>(
    name,
  );
  const quantite = Math.max(1, Number(value) || 1);
  return (
    <div className={styles.fieldRow}>
      <Text variant="deci" weight="bold" as="div">
        Nombre
      </Text>
      <div className={styles.stepper}>
        <IconButton
          icon={IconMinus}
          label="Retirer un"
          onClick={() => setValue(quantite - 1)}
          disabled={quantite <= 1}
        />
        <Text weight="bold" as="span" className={styles.stepperValue}>
          {quantite}
        </Text>
        <IconButton
          icon={IconPlus}
          label="Ajouter un"
          color="primary"
          onClick={() => setValue(quantite + 1)}
        />
      </div>
    </div>
  );
}

function EdlSubItems({
  inputNamePrefix,
  subItems,
  parentType,
}: {
  inputNamePrefix: string;
  subItems?: EdlSubItem[];
  parentType?: string;
}) {
  const count = subItems?.length ?? 0;
  return (
    <FieldArray
      name={`${inputNamePrefix}items`}
      render={(arrayHelpers) => (
        <div className={styles.field}>
          <div className={styles.fieldRow}>
            <Text variant="deci" weight="bold" as="div">
              Sous-éléments
            </Text>
            <Text variant="deci" as="div">
              {count > 0 ? `${count} au total` : "Facultatif"}
            </Text>
          </div>
          {subItems?.map((_, index) => (
            <div key={index} className={styles.subItem}>
              <TextInput
                name={`${inputNamePrefix}items[${index}].nom`}
                placeholder="Nom du sous-élément"
                marginForm=""
              />
              <ReactSelect
                name={`${inputNamePrefix}items[${index}].note`}
                options={ETAT_OPTIONS}
                placeholder="État"
                label="État"
                className={styles.subItemState}
              />
              <IconButton
                icon={IconX}
                label="Supprimer le sous-élément"
                color="danger"
                onClick={() => arrayHelpers.remove(index)}
              />
            </div>
          ))}
          <EdlAddButton
            onClick={() =>
              arrayHelpers.push({ ...EDL_ITEM_SUBITEM_VALUE, type: parentType })
            }
          >
            Ajouter un sous-élément
          </EdlAddButton>
        </div>
      )}
    />
  );
}

export default function EdlItemNomEtatInputs({
  isChild,
  item,
  inputNamePrefix = "",
  isTemplate,
  onDelete,
  onCopy,
  dragHandle,
  open = false,
  onToggle,
}: EdlItemNomEtatInputsProps) {
  const { edlMateriauxTemplates } = useEdlMateriauxTemplates() as {
    edlMateriauxTemplates: EdlMateriauxTemplate[];
  };
  const [{ value: note }, , { setValue: setNote }] = useField<
    string | number | undefined
  >(`${inputNamePrefix}note`);

  if (!isChild) {
    return (
      <div className={styles.roomInputs}>
        <TextInput
          name={`${inputNamePrefix}nom`}
          placeholder="Nom de la pièce"
          marginForm=""
        />
      </div>
    );
  }

  const materiauxTemplate = edlMateriauxTemplates?.find(
    (filtered) =>
      filtered.id === parseInt(String(item?.template_materiaux ?? "")),
  );
  const summary = elementSummary(item, isTemplate);
  const cardColorClass = ETAT_CARD_CLASS[String(note ?? "")];

  return (
    <div
      className={classnames(
        styles.element,
        open && styles.elementOpen,
        cardColorClass,
      )}
    >
      <div className={styles.row}>
        {dragHandle}
        {open ? (
          <TextInput
            name={`${inputNamePrefix}nom`}
            placeholder="Nom de l'élément"
            marginForm=""
            className={styles.nameInput}
            autoFocus={!item?.nom}
          />
        ) : (
          <button type="button" className={styles.rowMain} onClick={onToggle}>
            <Text
              weight="medium"
              as="span"
              color={item?.nom ? undefined : "muted"}
              className={styles.rowTitle}
            >
              {item?.nom || "Nouvel élément"}
            </Text>
            {summary && (
              <Text variant="deci" as="span" className={styles.rowSummary}>
                {summary}
              </Text>
            )}
          </button>
        )}
        {!isTemplate && <EtatStatus note={note} />}
        <div className={styles.rowActions}>
          {onCopy && (
            <IconButton
              icon={IconCopy}
              label="Copier l'élément"
              variant="plain"
              onClick={onCopy}
            />
          )}
          {onDelete && (
            <IconButton
              icon={IconTrash}
              label="Supprimer l'élément"
              variant="plain"
              onClick={onDelete}
            />
          )}
          <IconButton
            icon={open ? IconChevronDown : IconChevronRight}
            label={open ? "Replier l'élément" : "Déplier l'élément"}
            variant="plain"
            color="primary"
            onClick={onToggle}
          />
        </div>
      </div>

      {open && (
        <div className={styles.body}>
          {!isTemplate && (
            <div className={styles.field}>
              <Text variant="deci" weight="bold" as="div">
                État constaté
              </Text>
              <EdlEtatPills
                label="État constaté"
                value={note}
                onChange={setNote}
                allowClear
              />
            </div>
          )}

          {edlMateriauxTemplates?.length > 0 && (
            <ReactSelect
              name={`${inputNamePrefix}template_materiaux`}
              options={edlMateriauxTemplates.map((tmpl) => ({
                value: tmpl.id.toString(),
                label: tmpl.nom,
              }))}
              className={styles.selectField}
              label="Type de surface"
            />
          )}

          <QuantiteStepper name={`${inputNamePrefix}quantite`} />

          <EdlTagChips
            name={`${inputNamePrefix}materiaux`}
            label="Matériaux"
            options={materiauxTemplate?.materiaux ?? []}
          />

          <EdlTagChips
            name={`${inputNamePrefix}degradations`}
            label="Dégradations"
            options={materiauxTemplate?.degradations ?? []}
          />

          {!isTemplate && (
            <>
              <EdlSubItems
                inputNamePrefix={inputNamePrefix}
                subItems={item?.items}
                parentType={item?.type}
              />
              <div className={styles.field}>
                <div className={styles.fieldRow}>
                  <Text variant="deci" weight="bold" as="div">
                    Commentaire
                  </Text>
                  <Text variant="deci" as="div">
                    Facultatif
                  </Text>
                </div>
                <TextArea
                  name={`${inputNamePrefix}commentaire`}
                  placeholder="Ex : rayure de 15 cm sur le bas du battant, côté gauche"
                  rows={3}
                  marginForm=""
                />
              </div>
              <div className={styles.field}>
                <Text variant="deci" weight="bold" as="div">
                  Photos de l'élément
                </Text>
                <EdlPhotoField
                  path={`${inputNamePrefix}photos`}
                  text="Ajouter des photos de l'élément"
                  showCover={false}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
